/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:31:49                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-26 14:46:56                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * TimeTypeSelect: technician select component
    * useEffect: Manage isMulti parameter and display
    * getTimeTypes: Get technicians list
    * technicianChange: handle selector change 
*/

/* Imports */
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
/***/

/* Components */
/***/

/**
* TimeTypeSelect: technician select component
* Used to select a technician entity, it can open the 
* technician edit component to create or edit a technician.
* @param value - current value
* @param onChange - on value change callback
* @param disabled - field disabled state
* @param clearable - allow field clear feature
* @param isMulti - allow multi selection
* @param className - additional classNames
*/
export default function CompInfoTypeSelect({
  value,
  onChange,
  disabled,
  clearable,
  isMulti,
  isDetailed,
  className = ""
} : {
  value?: string | string[],
  onChange?: (value: any) => void,
  disabled?: boolean
  clearable?: boolean,
  isMulti?: boolean,
  isDetailed?: boolean,
  className?: string
}) {
  const { t } = useTranslation();
  const [valueState, setValueState] = useState<any>(null);

  /**
  * useEffect: Manage isMulti parameter and display
  * @param value - selector current value
  * @param isMulti - selector isMulti param
  */
  useEffect(() => {
    if (value && !isMulti) {
      const tmp = value as string;
      setValueState({value: tmp, label: `${tmp}`});
    }
    
    if (value && isMulti) {
      const vals = [];
      const tmp = value as string[];

      for (let i = 0; i < tmp.length; i++) {
        vals.push({value: tmp[i], label:  `${tmp[i]}`});   
      }

      setValueState(vals);
    }

    if (!value) setValueState(null);
  }, [value, isMulti]);
  /***/

  /**
  * getTimeTypes: Get technicians list
  * @return - technicians list
  */
  const getCompInfoTypes = (): Promise<EasyTech.IFormData<string>[]> => {
    return new Promise(resolve => {
      api.get("users/compinfos/types").then((ret) => {
        const tmp =  (ret as { inputs: string[], selectables: string[]});
        const options: string[] = [];
        tmp.inputs.forEach((e) => options.push(e));
        tmp.selectables.forEach((e) => options.push(e));
        return resolve(
          setSelectList<string>(options, (el) => (`${el}`))
        );
      }).catch((err) => {
        return resolve([]);
      });
    });
  };
  /***/

  /**
  * technicianChange: handle selector change 
  * @param value - new value
  */
  const compInfoTypeChange = ((value: any) => {
    const ret = isMulti ? [] : (value ? value.value : value);

    if (isMulti) {
      for (let i = 0; i < value.length; i++) {
        ret.push(value[i] ? value[i].value : value[i]);
      }
    }

    setValueState(value);
    if (onChange) onChange(ret);
  });
  /***/

  const SelectField = isMulti ? MultiCheckSelect : AsyncSelect;

  return (
    <div className={`flex ${className}`}>
      <SelectField  cacheOptions
                    defaultOptions
                    loadOptions={getCompInfoTypes}
                    value={valueState}
                    theme={reactSelectTheme}
                    onChange={compInfoTypeChange}
                    name="timetype"
                    placeholder={t("common:selectTimeType")}
                    className="w-full mr-2"
                    classNamePrefix="react-select"
                    isClearable={clearable}
                    isDisabled={disabled} 
                    isMulti={isMulti}
                    closeMenuOnSelect={!isMulti}
                    hideSelectedOptions={!isMulti}/>
    </div>
  );
}
/***/
